import { useEffect } from 'react';
import { useAppDemo } from '../context/AppDemoContext';

/**
 * Hook for tracking analytics in the app demo flow
 * @param {string} userId - User ID
 * @returns {Object} - Analytics tracking functions
 */
export const useAnalytics = (userId) => {
    const { currentStep, timeSpent } = useAppDemo();
    
    // Track page views
    useEffect(() => {
        const trackPageView = async () => {
            try {
                await fetch('/api/analytics/track', {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        event_type: 'page_view',
                        event_data: {
                            page: 'app_demo',
                            step: currentStep
                        }
                    })
                });
            } catch (error) {
                console.error('Failed to track page view:', error);
                // Non-fatal error, continue
            }
        };
        
        if (userId) {
            trackPageView();
        }
    }, [currentStep, userId]);
    
    // Track step completion when navigating between steps
    useEffect(() => {
        const prevStep = currentStep - 1;
        if (prevStep >= 0 && timeSpent[prevStep] && userId) {
            const trackStepCompletion = async () => {
                try {
                    await fetch('/api/analytics/track', {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            event_type: 'step_complete',
                            event_data: {
                                step_index: prevStep,
                                time_spent_ms: timeSpent[prevStep]
                            }
                        })
                    });
                } catch (error) {
                    console.error('Failed to track step completion:', error);
                    // Non-fatal error, continue
                }
            };
            
            trackStepCompletion();
        }
    }, [currentStep, timeSpent, userId]);
    
    // Function to track custom events
    const trackEvent = async (eventType, eventData = {}) => {
        if (!userId) return;
        
        try {
            await fetch('/api/analytics/track', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    event_type: eventType,
                    event_data: eventData
                })
            });
        } catch (error) {
            console.error(`Failed to track event ${eventType}:`, error);
            // Non-fatal error, continue
        }
    };
    
    return { trackEvent };
}; 